import { useState } from 'react';
import { MdArrowBack, MdPhone, MdSend } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../../axios';
import styles from './ForgotPassword.module.css';

const normalizePhone = (value) => {
    let phone = String(value || '')
        .replace(/[^\d+]/g, '')
        .trim();

    if (!phone) return '';

    if (phone.startsWith('+')) phone = phone.substring(1);
    if (phone.startsWith('0')) phone = `62${phone.substring(1)}`;
    else if (phone.startsWith('8')) phone = `62${phone}`;

    return phone;
};

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({ login: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [fieldError, setFieldError] = useState('');

    const handleChange = (e) => {
        setForm({ login: e.target.value });
        setError('');
        setFieldError('');
        setSuccess('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const loginValue = form.login.trim();

        if (!loginValue) {
            setFieldError('Nomor WhatsApp atau email wajib diisi.');
            return;
        }

        setLoading(true);
        setError('');
        setFieldError('');
        setSuccess('');

        try {
            const payloadLogin = loginValue.includes('@') ? loginValue : normalizePhone(loginValue);

            const response = await axiosInstance.post(
                '/auth/forgot-password',
                { login: payloadLogin },
                {
                    validateStatus: (status) => status < 500,
                }
            );

            const responseData = response?.data || {};

            if (!responseData?.success) {
                const apiMessage = responseData?.message || '';

                if (response.status === 422) {
                    setFieldError(apiMessage || 'Data yang Anda masukkan belum valid.');
                    return;
                }

                if (response.status === 404) {
                    setError(apiMessage || 'Akun tidak ditemukan.');
                    return;
                }

                if (response.status === 429) {
                    setError(apiMessage || 'Terlalu banyak percobaan. Coba lagi beberapa saat.');
                    return;
                }

                setError(apiMessage || 'Gagal mengirim instruksi reset password.');
                return;
            }

            sessionStorage.setItem('mp_reset_login', payloadLogin);
            sessionStorage.setItem('mp_reset_channel', responseData?.data?.channel || '');

            setSuccess(responseData?.message || 'Kode verifikasi sudah dikirim. Silakan cek email atau WhatsApp Anda.');
            setForm({ login: '' });

            navigate('/reset-password', {
                state: {
                    login: payloadLogin,
                    channel: responseData?.data?.channel || '',
                },
            });
        } catch (err) {
            setError(err?.apiMessage || 'Terjadi kesalahan. Silakan coba lagi.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.page}>
            <section className={styles.card}>
                <div className={styles.header}>
                    <div className={styles.logoWrapper}>
                        <img src="/logo-dark.png" alt="Logo" className={styles.logo} />
                    </div>
                    <h1 className={styles.title}>Lupa Password?</h1>
                    <p className={styles.subtitle}>
                        Masukkan email atau nomor WhatsApp yang terdaftar untuk menerima kode verifikasi.
                    </p>
                </div>

                {error && (
                    <div className={styles.errorBox} role="alert" aria-live="polite">
                        {error}
                    </div>
                )}

                {success && (
                    <div className={styles.successBox} role="status" aria-live="polite">
                        {success}
                    </div>
                )}

                <form onSubmit={handleSubmit} noValidate className={styles.form}>
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="login">
                            Nomor WhatsApp / Email
                        </label>

                        <div className={styles.inputWrap}>
                            <MdPhone className={styles.iconLeft} size={18} />
                            <input
                                id="login"
                                type="text"
                                name="login"
                                className={`${styles.input} ${fieldError ? styles.inputError : ''}`}
                                placeholder="08xx-xxxx-xxxx atau email"
                                value={form.login}
                                onChange={handleChange}
                                autoComplete="username"
                                autoCapitalize="none"
                                autoCorrect="off"
                                aria-invalid={!!fieldError}
                                aria-describedby={fieldError ? 'forgot-error' : undefined}
                            />
                        </div>

                        {fieldError && (
                            <span id="forgot-error" className={styles.fieldError} role="alert">
                                {fieldError}
                            </span>
                        )}
                    </div>

                    <button type="submit" className={styles.submitBtn} disabled={loading}>
                        {loading ? (
                            <span className={styles.spinner} aria-hidden="true" />
                        ) : (
                            <>
                                <MdSend size={18} />
                                Kirim Kode Verifikasi
                            </>
                        )}
                    </button>
                </form>

                <div className={styles.footer}>
                    <Link to="/login" className={styles.backLink}>
                        <MdArrowBack size={16} /> Kembali ke login
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default ForgotPassword;
